import { useState } from "react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { Reveal, Overline } from "@/components/Reveal";
import { api, formatApiErrorDetail } from "@/lib/api";
import { VOLUNTEERS } from "@/assets";

export const Volunteer = () => {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", postal_code: "", message: "" });
  const [interests, setInterests] = useState([]);
  const [loading, setLoading] = useState(false);

  const toggle = (i) =>
    setInterests((prev) => (prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i]));

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/volunteers", { ...form, interests });
      toast.success(t.volunteer.success);
      setForm({ name: "", email: "", phone: "", postal_code: "", message: "" });
      setInterests([]);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t.volunteer.error);
    } finally {
      setLoading(false);
    }
  };

  const input =
    "w-full bg-transparent border border-white/20 rounded-sm px-4 py-3.5 text-white placeholder-white/35 focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/30 transition-colors";

  return (
    <section id="volunteer" data-testid="volunteer-section" className="py-24 md:py-40 bg-[#070707]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-16 items-start">
        <div>
          <Reveal><Overline>{t.volunteer.overline}</Overline></Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display uppercase text-5xl sm:text-7xl leading-[0.88] mt-4">{t.volunteer.title}</h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="text-white/60 text-lg mt-6 max-w-md font-light">{t.volunteer.lead}</p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-10 overflow-hidden rounded-sm hidden lg:block">
              <img src={VOLUNTEERS} alt="Volunteers" className="w-full h-80 object-cover grayscale hover:grayscale-0 transition-all duration-700" />
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.2}>
          <form onSubmit={submit} data-testid="volunteer-form" className="glass rounded-xl p-6 md:p-10 space-y-5">
            <div className="grid sm:grid-cols-2 gap-5">
              <input required data-testid="vol-name" className={input} placeholder={t.volunteer.name} value={form.name} onChange={set("name")} />
              <input required type="email" data-testid="vol-email" className={input} placeholder={t.volunteer.email} value={form.email} onChange={set("email")} />
              <input data-testid="vol-phone" className={input} placeholder={t.volunteer.phone} value={form.phone} onChange={set("phone")} />
              <input data-testid="vol-postal" className={input} placeholder={t.volunteer.postal} value={form.postal_code} onChange={set("postal_code")} />
            </div>

            <div>
              <p className="text-sm text-white/60 mb-3 uppercase tracking-wider">{t.volunteer.interestsLabel}</p>
              <div className="flex flex-wrap gap-2">
                {t.volunteer.interests.map((i) => (
                  <button
                    type="button"
                    key={i}
                    data-testid={`vol-interest-${i}`}
                    onClick={() => toggle(i)}
                    className={`px-4 py-2 rounded-full text-sm border transition-colors ${
                      interests.includes(i)
                        ? "bg-purple-600 border-purple-600 text-white"
                        : "border-white/20 text-white/70 hover:border-purple-500"
                    }`}
                  >
                    {i}
                  </button>
                ))}
              </div>
            </div>

            <textarea rows={3} data-testid="vol-message" className={input} placeholder={t.volunteer.message} value={form.message} onChange={set("message")} />

            <button
              type="submit"
              disabled={loading}
              data-testid="vol-submit"
              className="w-full py-4 rounded-full bg-white text-black font-semibold tracking-wide hover:bg-purple-500 hover:text-white transition-colors disabled:opacity-50"
            >
              {loading ? "…" : t.volunteer.submit}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
